/**
 * The `summary` the coach portal's Career snapshot shows on its folded
 * header (issue #526).
 *
 * #526 folds the snapshot (`CoachCareerSnapshot.jsx`) behind a
 * `SectionCard`, and the header-state obligation (#378: a fold must say
 * what is inside it) means the folded card carries a headline derived from
 * the SAME `/stats/summary` payload the open card renders. Two rules, both
 * from the issue:
 *
 *  - HONESTY BEFORE HEADLINES. While the payload is loading, or after it
 *    failed, the summary is an em dash — the explicit "nothing to report"
 *    every settings fold uses — never a fabricated number. A "0 runs"
 *    badge over a request that has not answered is journey 8's defect
 *    (a plausible zero standing in for "we don't know") on a new surface.
 *  - THE HEADLINE SHAPE, decided per the issue's two candidates: with
 *    matches on record it is `"412 runs · 17 wkts"`; with none it is
 *    `"0 matches"` — runs-and-wickets would be three zeros claiming a
 *    career that does not exist yet, while an explicit zero match count is
 *    the count-style "nothing yet" #378 asks for.
 *
 * The payload reflects whatever filters the coach set while the card was
 * open (season/competition/team), so a narrowed summary describes the
 * narrowed scope — the same numbers the open card would show. Filters
 * reset per player by the `key={player_id}` remount at the call site.
 *
 * A plain function with no imports, out of the component, for the same
 * reason as `sectionCardState.js`: this repo mounts no components in unit
 * tests, and a rule buried in a render body gets string matches instead of
 * a behavioural test.
 *
 * @param {{ d: object | null, failed: boolean }} state the snapshot's
 *   fetch state: `d` is the `/stats/summary` payload or null, `failed` the
 *   #271 third state.
 * @returns {string} a badge string, never empty
 */
export function careerSnapshotSummary({ d, failed }) {
  if (failed || d == null) return "—";
  const { matches, total_runs: runs, total_wickets: wickets } = d;
  // A payload missing its numbers is not a payload to headline — same
  // posture as failed. `computeStatsSummary` never emits these as null for
  // a loaded player, so this guard should be unreachable; it exists so a
  // server change degrades to an honest dash, not "undefined runs".
  if (!Number.isFinite(matches) || !Number.isFinite(runs) || !Number.isFinite(wickets)) return "—";
  if (matches === 0) return "0 matches";
  return `${runs} runs · ${wickets} wkts`;
}
